import React from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/types';
import ResourcedEmoji from '@atlaskit/emoji/resourced-emoji';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import type { TabParameters } from '../nativeTabsTypes.ts';
import { getTabColorAppearance } from '../tab-color/tabColorDefinitions.ts';
import { isStandardTabColorPaletteEnabled } from '../tab-color/tabColorFeatureGate.ts';

type PdfHeaderColorCustomProperties = React.CSSProperties & {
	'--native-tabs-pdf-header-background-color'?: string;
};

const styles = cssMap({
	root: {
		width: '100%',
	},
	section: {
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		overflow: 'hidden',
	},
	header: {
		alignItems: 'center',
		color: token('color.text'),
		display: 'flex',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	defaultHeader: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
	coloredHeader: {
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors; remove once cssMap supports arbitrary CSS custom property values.
		backgroundColor: 'var(--native-tabs-pdf-header-background-color)',
	},
	emoji: {
		alignItems: 'center',
		display: 'inline-flex',
		height: '20px',
		justifyContent: 'center',
		width: '20px',
	},
	body: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
	},
});

const getFrameBody = (frame: React.ReactNode): React.ReactNode => {
	if (React.isValidElement<{ children?: React.ReactNode }>(frame)) {
		return frame.props.children;
	}

	return frame;
};

const getFrameBodiesFromChildrenContainer = (
	childrenContainer: React.ReactNode,
): React.ReactNode[] => {
	if (!React.isValidElement<{ children?: React.ReactNode }>(childrenContainer)) {
		return [];
	}

	return React.Children.toArray(childrenContainer.props.children).map(getFrameBody);
};

type NativeTabsPdfRendererProps = {
	childrenContainer: React.ReactNode;
	emojiProvider?: Promise<EmojiProvider>;
	rootRef?: React.RefObject<HTMLDivElement>;
	tabs: ReadonlyArray<TabParameters>;
};

export const NativeTabsPdfRenderer: {
	({
		childrenContainer,
		emojiProvider,
		rootRef,
		tabs,
	}: NativeTabsPdfRendererProps): React.JSX.Element;
	displayName: string;
} = ({ childrenContainer, emojiProvider, rootRef, tabs }: NativeTabsPdfRendererProps) => {
	const appearanceMode = isStandardTabColorPaletteEnabled() ? 'standard' : 'legacy';
	const frameBodies = getFrameBodiesFromChildrenContainer(childrenContainer);

	return (
		<Box ref={rootRef} xcss={styles.root} testId="native-tabs-pdf-export">
			<Stack space="space.200">
				{tabs.map((tab, index) => {
					const headerBackgroundColor = tab.color
						? getTabColorAppearance(tab.color, appearanceMode).tabPill.inactive.backgroundColor
						: undefined;
					return (
						<Box
							key={tab.id}
							xcss={styles.section}
							testId={`native-tabs-pdf-export-section-${index}`}
						>
							<Box
								xcss={cx(
									styles.header,
									headerBackgroundColor ? styles.coloredHeader : styles.defaultHeader,
								)}
								style={
									{
										'--native-tabs-pdf-header-background-color': headerBackgroundColor,
									} as PdfHeaderColorCustomProperties
								}
								testId={`native-tabs-pdf-export-header-${index}`}
							>
								{tab.emoji && emojiProvider ? (
									<Box xcss={styles.emoji}>
										<ResourcedEmoji
											emojiProvider={emojiProvider}
											emojiId={{ id: tab.emoji, shortName: tab.emoji }}
											fitToHeight={20}
											showTooltip={false}
										/>
									</Box>
								) : null}
								{tab.title}
							</Box>
							<Box xcss={styles.body}>{frameBodies[index]}</Box>
						</Box>
					);
				})}
			</Stack>
		</Box>
	);
};

NativeTabsPdfRenderer.displayName = 'NativeTabsPdfRenderer';
